import { z } from 'zod';
import { CustomFieldValueSchema } from './custom-field-value.dto';
import { inferApiResultSchema, inferPaginatedApiResponse } from './infer-api';
import { DescriptionMaxLength, type GetAllParamsDto } from './types.dto';

export const demandTitleLimit = 300;
export const demandSummaryLimit = 500;

export const demandTypeNameLimit = 100;

export const DemandTypeSchema = z.object({
  id: z.number(),
  name: z.string().trim().min(1).max(demandTypeNameLimit),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type DemandTypeDto = z.infer<typeof DemandTypeSchema>;

// POST /api/v1/fim/demand-types
export const CreateDemandTypeSchema = z.object({
  name: z.string().trim().min(1).max(demandTypeNameLimit),
});
export type CreateDemandTypeDto = z.infer<typeof CreateDemandTypeSchema>;

// PUT /api/v1/fim/demand-types/:id
export const UpdateDemandTypeSchema = z.object({
  name: z.string().trim().min(1).max(demandTypeNameLimit),
});
export type UpdateDemandTypeDto = z.infer<typeof UpdateDemandTypeSchema>;

export const GetDemandTypesResponseSchema = inferApiResultSchema(z.array(DemandTypeSchema));
export type GetDemandTypesResponseDto = z.infer<typeof GetDemandTypesResponseSchema>;

export const CreateDemandTypeResponseSchema = inferApiResultSchema(DemandTypeSchema);
export type CreateDemandTypeResponseDto = z.infer<typeof CreateDemandTypeResponseSchema>;

export const UpdateDemandTypeResponseSchema = inferApiResultSchema(DemandTypeSchema);
export type UpdateDemandTypeResponseDto = z.infer<typeof UpdateDemandTypeResponseSchema>;

export const DeleteDemandTypeResponseSchema = inferApiResultSchema(
  z.object({ id: z.number().int().positive() }),
);
export type DeleteDemandTypeResponseDto = z.infer<typeof DeleteDemandTypeResponseSchema>;

export const DemandStatusType = {
  NotStarted: 'NOT_STARTED',
  InProgress: 'IN_PROGRESS',
  Ready: 'READY',
  Done: 'DONE',
} as const;
export type DemandStatusTypeT = (typeof DemandStatusType)[keyof typeof DemandStatusType];
export const DemandStatusTypeSchema = z.nativeEnum(DemandStatusType);

export const DemandStatusSchema = z.object({
  id: z.number(),
  accountId: z.string().max(50),
  name: z.string().trim().min(1).max(255),
  color: z.string().max(20),
  demandStatusType: DemandStatusTypeSchema,
  sortOrder: z.number(),
  isAccountDefault: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  archivedAt: z.coerce.date().nullish(),
});
export type DemandStatusDto = z.infer<typeof DemandStatusSchema>;

export const DemandSchema = z.object({
  id: z.number(),
  accountId: z.string().max(50),
  ownerId: z.string().max(50),
  assigneeId: z.string().max(50).nullish(),
  title: z.string().trim().min(1).max(demandTitleLimit),
  description: z.string().max(DescriptionMaxLength).nullish(),
  summary: z.string().trim().max(demandSummaryLimit).nullish(),
  statusId: z.number(),
  typeId: z.number().nullish(),
  fields: z.array(CustomFieldValueSchema).default([]),
  updatedAt: z.coerce.date().nullish(),
  createdAt: z.coerce.date().nullish(),
  deletedAt: z.coerce.date().nullish(),
  archivedAt: z.coerce.date().nullish(),
  statusChangedAt: z.coerce.date().nullish(),
  originatingIdeaId: z.number().nullish(),
  loopId: z.string().max(50).nullish(),
});
export type DemandDto = z.infer<typeof DemandSchema>;

export type GetAllDemandsDto = GetAllParamsDto & { statusId?: number };

// GET /api/v1/fim/demands/:id
export const GetDemandByIdResponseSchema = inferApiResultSchema(DemandSchema);
export type GetDemandByIdResponseDto = z.infer<typeof GetDemandByIdResponseSchema>;

// GET /api/v1/fim/demands
export const GetDemandsResponseSchema = inferPaginatedApiResponse(z.array(DemandSchema));
export type GetDemandsResponseDto = z.infer<typeof GetDemandsResponseSchema>;

// POST /api/v1/fim/demands/getMatrixDemands — trimmed to the fields the matrix view needs
export const MatrixDemandSummarySchema = DemandSchema.pick({
  id: true,
  accountId: true,
  ownerId: true,
  assigneeId: true,
  title: true,
  description: true,
  fields: true,
});
export type MatrixDemandSummaryDto = z.infer<typeof MatrixDemandSummarySchema>;

export const GetMatrixDemandsResponseSchema = inferApiResultSchema(
  z.array(MatrixDemandSummarySchema),
);
export type GetMatrixDemandsResponseDto = z.infer<typeof GetMatrixDemandsResponseSchema>;

// POST /api/v1/fim/demands
export const CreateDemandSchema = DemandSchema.omit({
  id: true,
  accountId: true,
  ownerId: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  archivedAt: true,
  statusChangedAt: true,
})
  .partial({ fields: true, statusId: true })
  .extend({ loopId: z.string().min(1).max(50) });
export type CreateDemandDto = z.infer<typeof CreateDemandSchema>;
export const CreateDemandResponseSchema = inferApiResultSchema(DemandSchema);
export type CreateDemandResponseDto = z.infer<typeof CreateDemandResponseSchema>;

// PUT /api/v1/fim/demands/:id
export const UpdateDemandSchema = DemandSchema.omit({
  id: true,
  accountId: true,
  ownerId: true,
  statusId: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  archivedAt: true,
  statusChangedAt: true,
})
  .partial()
  // Override `fields` to drop the `.default([])` inherited from DemandSchema. Otherwise a
  // partial update that omits `fields` parses to `fields: []`, which the repository then
  // persists, erasing all stored custom-field values (e.g. Priority Framework).
  .extend({ fields: z.array(CustomFieldValueSchema).optional() });
export type UpdateDemandDto = z.infer<typeof UpdateDemandSchema>;
export const UpdateDemandResponseSchema = inferApiResultSchema(DemandSchema);
export type UpdateDemandResponseDto = z.infer<typeof UpdateDemandResponseSchema>;

// DELETE /api/v1/fim/demands/:id
export const DeleteDemandResponseSchema = inferApiResultSchema(DemandSchema);
export type DeleteDemandResponseDto = z.infer<typeof DeleteDemandResponseSchema>;

// PUT /api/v1/fim/demands/:id/archive
export const ArchiveDemandResponseSchema = inferApiResultSchema(DemandSchema);
export type ArchiveDemandResponseDto = z.infer<typeof ArchiveDemandResponseSchema>;

// PUT /api/v1/fim/demands/:id/restore
export const RestoreDemandResponseSchema = inferApiResultSchema(DemandSchema);
export type RestoreDemandResponseDto = z.infer<typeof RestoreDemandResponseSchema>;

// `demandStatusType` is restricted to the system-defined set above (also enforced by a DB CHECK
// constraint). Accounts may rename, recolor, reorder, add, and delete statuses, but every status
// must map to one of the four system-defined types.

export const demandStatusNameLimit = 255;
export const demandStatusColorLimit = 20;

// POST /api/v1/fim/demand-statuses
export const CreateDemandStatusSchema = z.object({
  name: z.string().trim().min(1).max(demandStatusNameLimit),
  color: z.string().trim().min(1).max(demandStatusColorLimit),
  demandStatusType: DemandStatusTypeSchema,
  isAccountDefault: z.boolean().optional(),
});
export type CreateDemandStatusDto = z.infer<typeof CreateDemandStatusSchema>;

// PUT /api/v1/fim/demand-statuses/:id
export const UpdateDemandStatusSchema = z.object({
  name: z.string().trim().min(1).max(demandStatusNameLimit).optional(),
  color: z.string().trim().min(1).max(demandStatusColorLimit).optional(),
  demandStatusType: DemandStatusTypeSchema.optional(),
  isAccountDefault: z.boolean().optional(),
});
export type UpdateDemandStatusDto = z.infer<typeof UpdateDemandStatusSchema>;

// PUT /api/v1/fim/demand-statuses/reorder
export const ReorderDemandStatusesSchema = z.object({
  orderedIds: z.array(z.number().int().positive()).min(1),
});
export type ReorderDemandStatusesDto = z.infer<typeof ReorderDemandStatusesSchema>;

// PUT /api/v1/fim/demands/:id/status
export const ChangeDemandStatusSchema = z.object({
  statusId: z.number().int().positive(),
});
export type ChangeDemandStatusDto = z.infer<typeof ChangeDemandStatusSchema>;

export const GetDemandStatusesResponseSchema = inferApiResultSchema(z.array(DemandStatusSchema));
export type GetDemandStatusesResponseDto = z.infer<typeof GetDemandStatusesResponseSchema>;

export const GetDemandStatusByIdResponseSchema = inferApiResultSchema(DemandStatusSchema);
export type GetDemandStatusByIdResponseDto = z.infer<typeof GetDemandStatusByIdResponseSchema>;

export const CreateDemandStatusResponseSchema = inferApiResultSchema(DemandStatusSchema);
export type CreateDemandStatusResponseDto = z.infer<typeof CreateDemandStatusResponseSchema>;

export const UpdateDemandStatusResponseSchema = inferApiResultSchema(DemandStatusSchema);
export type UpdateDemandStatusResponseDto = z.infer<typeof UpdateDemandStatusResponseSchema>;

export const ReorderDemandStatusesResponseSchema = inferApiResultSchema(
  z.array(DemandStatusSchema),
);
export type ReorderDemandStatusesResponseDto = z.infer<typeof ReorderDemandStatusesResponseSchema>;

export const ChangeDemandStatusResponseSchema = inferApiResultSchema(DemandSchema);
export type ChangeDemandStatusResponseDto = z.infer<typeof ChangeDemandStatusResponseSchema>;

// The `entity` value carried by every demand activity-log changeLog entry, and the sole identifier
// of what a feed entry represents. Shared here rather than left as string literals in
// `apps/fim`'s demand.service.ts so the frontend can switch on the vocabulary instead of matching
// strings, and so a wording change cannot silently desynchronise writer from reader.
//
// The frontend resolves each value against two translation key spaces - see
// resolveEntityLabelKey.ts and ConversationsAndActivity.tsx:
//   <root>.attributeLabels.<entity>                     -> the noun
//   <root>.activityLogs.<entity lowercased>.<LOGTYPE>   -> the verb
// `<root>` is `demand` for these values and `intake` for the rest of the module's vocabulary;
// resolveKeyRoot picks it by probing the resource, so neither side hardcodes the split.
// Adding a value here without adding both keys makes the feed render a generic fallback.
export const DemandActivityEntity = {
  root: 'demand',
  title: 'demand.title',
  description: 'demand.description',
  summary: 'demand.summary',
  fields: 'demand.fields',
  originatingIdeaId: 'demand.originatingIdeaId',
  assigneeId: 'demand.assigneeId',
  /** @deprecated dropped demand_roadmap_items; kept so historical activity-log rows still render. */
  roadmapItem: 'demand.roadmapItem',
  attachment: 'demand.attachment',
  /**
   * A sibling of `attachment`, not a child of it. Evidence is the general concept - a file, a linked
   * idea, later a market signal - and an attachment is one kind of it, so nesting would make the
   * general case read as a detail of the specific one. See the note on `reviewerDecision` for what
   * nesting costs.
   */
  evidence: 'demand.evidence',
  reviewer: 'demand.reviewer',
  /**
   * A sibling of `reviewer` rather than a child of it. `resolveEntityLabelKey` returns null for an
   * entity whose attributeLabels entry is an object without a `.label` child, so nesting this under
   * `demand.reviewer` would demote the existing reviewer label and break the ADD/REMOVE entries.
   */
  reviewerDecision: 'demand.reviewerDecision',
  comment: 'demand.comment',
  /** A guided-path step's completion, flipped implicitly by whatever routine owns its data. */
  checklistItem: 'demand.checklistItem',
} as const;

export type DemandActivityEntityValue =
  (typeof DemandActivityEntity)[keyof typeof DemandActivityEntity];
